import * as React from 'react';

import {
    Alert,
    Badge,
    Button,
    ProgressBar,
    Skeleton,
    Surface,
    notify
} from '@/components/ui';

import {
    CompactVariantPreview,
    createFamily,
    LabeledPreviewCell,
    LabeledPreviewGrid,
    PreviewRow,
    PreviewStack,
    PreviewSurface,
    VariantGallery,
    VariantPreview,
    withStorySources
} from './helpers';

const AlertStory = () => (
    <VariantGallery columns={2}>
        {['info', 'success', 'warning', 'destructive'].map(tone => (
            <CompactVariantPreview key={tone} meta={[{ label: 'tone', value: tone }]}>
                <Alert
                    description={`Preview of the \`${tone}\` alert tone.`}
                    title={tone.capitalize()}
                    tone={tone}
                />
            </CompactVariantPreview>
        ))}
    </VariantGallery>
);

const AlertHero = () => (
    <Alert
        description="Inline contextual feedback. Pick the tone that matches the message."
        dismissible
        title="Heads up"
        tone="info"
    />
);

const AlertDismissibleStory = () => {
    const [attempt, setAttempt] = React.useState(0);

    return (
        <PreviewStack>
            <PreviewRow>
                <Button onClick={() => setAttempt(value => value + 1)}>Show alert</Button>
            </PreviewRow>
            <PreviewSurface>
                <Alert
                    key={attempt}
                    description="This preview uses the animated alert wrapper and can be dismissed locally."
                    dismissible
                    duration={5000}
                    title="Deployment notice"
                    tone="warning"
                />
            </PreviewSurface>
        </PreviewStack>
    );
};

const AlertDismissibleHero = () => (
    <Alert
        description="This preview uses the animated alert wrapper and can be dismissed locally."
        dismissible
        title="Deployment notice"
        tone="warning"
    />
);

const BadgeStory = () => (
    <PreviewStack>
        <VariantPreview className="tw-items-stretch" meta={[{ label: 'size', value: 'medium (default)' }]}>
            <LabeledPreviewGrid minColumnWidth="120px">
                {['neutral', 'primary', 'secondary', 'info', 'success', 'warning', 'destructive'].map(tone => (
                    <LabeledPreviewCell key={tone} label={tone}>
                        <Badge tone={tone} label="Label" />
                    </LabeledPreviewCell>
                ))}
            </LabeledPreviewGrid>
        </VariantPreview>
        <VariantPreview className="tw-items-stretch" meta={[{ label: 'size', value: 'small' }]}>
            <LabeledPreviewGrid minColumnWidth="120px">
                {['neutral', 'primary', 'secondary', 'success'].map(tone => (
                    <LabeledPreviewCell key={tone} label={tone}>
                        <Badge tone={tone} label="Label" size="small" />
                    </LabeledPreviewCell>
                ))}
            </LabeledPreviewGrid>
        </VariantPreview>
    </PreviewStack>
);

const BadgeHero = () => (
    <Badge tone="success" label="Online" />
);

const ProgressBarStory = () => (
    <VariantGallery>
        {['primary', 'secondary'].flatMap(tone => (
            [20, 55, 90].map(value => (
                <CompactVariantPreview key={`${tone}-${value}`} meta={[{ label: 'tone', value: tone }, { label: 'value', value: `${value}%` }]}>
                    <ProgressBar tone={tone} value={value} />
                </CompactVariantPreview>
            ))
        ))}
    </VariantGallery>
);

const ProgressBarHero = () => (
    <ProgressBar value={55} />
);

const SkeletonStory = () => (
    <PreviewStack>
        <VariantPreview meta={[{ label: 'tone', value: 'subtle' }, { label: 'parent', value: 'background' }]}>
            <Surface tone="neutral">
                <div className="tw-flex tw-flex-col tw-gap-3">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-h-12 tw-w-12"><Skeleton circle /></div>
                        <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-2">
                            <div className="tw-h-4 tw-w-1/2"><Skeleton /></div>
                            <div className="tw-h-4 tw-w-3/4"><Skeleton /></div>
                        </div>
                    </div>
                    <div className="tw-h-5 tw-w-1/3"><Skeleton /></div>
                    <div className="tw-h-20 tw-w-full"><Skeleton /></div>
                </div>
            </Surface>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'tone', value: 'strong' }, { label: 'parent', value: 'background' }]}>
            <Surface tone="neutral">
                <div className="tw-flex tw-flex-col tw-gap-3">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-h-12 tw-w-12"><Skeleton circle tone="strong" /></div>
                        <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-2">
                            <div className="tw-h-4 tw-w-1/2"><Skeleton tone="strong" /></div>
                            <div className="tw-h-4 tw-w-3/4"><Skeleton tone="strong" /></div>
                        </div>
                    </div>
                    <div className="tw-h-5 tw-w-1/3"><Skeleton tone="strong" /></div>
                    <div className="tw-h-20 tw-w-full"><Skeleton tone="strong" /></div>
                </div>
            </Surface>
        </VariantPreview>
    </PreviewStack>
);

const SkeletonHero = () => (
    <Surface tone="neutral">
        <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-h-12 tw-w-12"><Skeleton circle tone="strong" /></div>
                <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-2">
                    <div className="tw-h-4 tw-w-1/2"><Skeleton tone="strong" /></div>
                    <div className="tw-h-4 tw-w-3/4"><Skeleton tone="strong" /></div>
                </div>
            </div>
            <div className="tw-h-5 tw-w-1/3"><Skeleton tone="strong" /></div>
            <div className="tw-h-20 tw-w-full"><Skeleton tone="strong" /></div>
        </div>
    </Surface>
);

const NotifyStory = () => (
    <VariantGallery className="xl:tw-grid-cols-3">
        <CompactVariantPreview meta={[{ label: 'position', value: 'bottom-right' }]}>
            <Button onClick={() => notify.success('Saved changes', { description: 'Default position.' })}>
                bottom-right
            </Button>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'position', value: 'top-right' }]}>
            <Button appearance="outline" tone="neutral" onClick={() => notify.info('Heads up', { description: 'Slides from right.', position: 'top-right' })}>
                top-right
            </Button>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'position', value: 'top-left' }]}>
            <Button appearance="outline" tone="neutral" onClick={() => notify.warning('Review required', { description: 'Slides from left.', position: 'top-left' })}>
                top-left
            </Button>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'position', value: 'bottom-left' }]}>
            <Button appearance="outline" tone="neutral" onClick={() => notify.error('Request failed', { description: 'Slides from left.', position: 'bottom-left' })}>
                bottom-left
            </Button>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'position', value: 'top-center' }]}>
            <Button appearance="outline" tone="neutral" onClick={() => notify.info('Centered', { description: 'Slides from top.', position: 'top-center' })}>
                top-center
            </Button>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'position', value: 'bottom-center' }]}>
            <Button appearance="outline" tone="neutral" onClick={() => notify.success('Centered', { description: 'Slides from bottom.', position: 'bottom-center' })}>
                bottom-center
            </Button>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'stack', value: '3 rapid' }]}>
            <Button appearance="outline" tone="neutral" onClick={() => {
                notify.info('First notification');
                notify.success('Second notification');
                notify.warning('Third notification');
            }}>
                stack (3x)
            </Button>
        </CompactVariantPreview>
        <CompactVariantPreview meta={[{ label: 'duration', value: '10s' }]}>
            <Button appearance="outline" tone="neutral" onClick={() => notify.info('Slow dismiss', { description: 'Stays for 10 seconds.', duration: 10000 })}>
                long duration
            </Button>
        </CompactVariantPreview>
    </VariantGallery>
);

const NotifyHero = () => (
    <Button onClick={() => notify.success('Saved changes', { description: 'Your changes have been persisted.' })}>
        Trigger toast
    </Button>
);

const feedbackFamilies = [
    createFamily({
        id: 'alert',
        label: 'Alert',
        description: 'Inline contextual feedback with semantic tones, optional dismissibility, and auto-dismiss.',
        subcomponents: ['Alert'],
        stories: [
            {
                id: 'alert-variants',
                title: 'Tones',
                description: 'Info, success, warning and destructive variants.',
                render: AlertStory,
                hero: AlertHero
            },
            {
                id: 'alert-dismissible',
                title: 'Dismissible alert',
                description: 'X button in the corner; optional auto-dismiss via duration.',
                render: AlertDismissibleStory,
                hero: AlertDismissibleHero
            }
        ]
    }),
    createFamily({
        id: 'badge',
        label: 'Badge',
        description: 'Compact label chip. Brand tones (neutral / primary / secondary) for identity / category labels; status tones (info / success / warning / destructive) for state communication. Replaces the former StatusPill.',
        subcomponents: ['Badge'],
        stories: [
            {
                id: 'badge-variants',
                title: 'Variants',
                render: BadgeStory,
                hero: BadgeHero
            }
        ]
    }),
    createFamily({
        id: 'progress-bar',
        label: 'ProgressBar',
        description: 'Slim horizontal progress indicator. Two tones — `primary` (default) and `secondary` — share the system\'s brand palette.',
        subcomponents: ['ProgressBar'],
        stories: [
            {
                id: 'progress-bar-values',
                title: 'ProgressBar tones and values',
                render: ProgressBarStory,
                hero: ProgressBarHero
            }
        ]
    }),
    createFamily({
        id: 'skeleton',
        label: 'Skeleton',
        description: 'Closed paint primitive for loading placeholders. Caller wraps with a sized container; Skeleton fills it with an animated pulse. `tone="subtle"` (`bg-muted`) for white surfaces, `tone="strong"` (`bg-border`) for muted/tinted surfaces. `circle` flips the shape from rounded bar to circle (avatar placeholders).',
        subcomponents: ['Skeleton'],
        stories: [
            {
                id: 'skeleton-layouts',
                title: 'Tone × shape',
                render: SkeletonStory,
                hero: SkeletonHero
            }
        ]
    }),
    createFamily({
        id: 'toast',
        label: 'Toast',
        description: 'Delivery mechanism for transient floating Alerts. notify renders an <Alert elevated /> inside Sonner. The visual contract lives in Alert; this family is about positioning, stacking, and auto-dismiss.',
        subcomponents: ['notify', 'ToastProvider'],
        stories: [
            {
                id: 'toast-notify',
                title: 'notify helper',
                description: 'Imperative notifications with position, slide direction and stack control. The visual gallery lives in the Alert family.',
                render: NotifyStory,
                hero: NotifyHero
            }
        ]
    })
];

const feedbackStorySections = feedbackFamilies.map(withStorySources);

export { feedbackStorySections };
